import React from 'react';
import { Link } from 'react-router-dom';
import {
  Inbox,
  FileText,
  FileStack,
  ShoppingBag,
  Users,
  CheckSquare,
  Clock,
  CalendarCheck,
} from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { ROUTES } from '@/routes/paths';
import type { Scope } from './types';

interface KpiItem {
  key: string;
  label: string;
  count: number | string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
  link: string;
}

interface SalesEmployeeKpisProps {
  scope: Scope;
  onScopeChange: (scope: Scope) => void;
  canChangeScope: boolean;
  kpiCounts?: {
    openEnquiries?: number;
    openQuotations?: number;
    openProforma?: number;
    openSales?: number;
    totalContacts?: number;
    openTasks?: number;
    overdueFollowups?: number;
    dueToday?: number;
  };
}

export const SalesEmployeeKpis: React.FC<SalesEmployeeKpisProps> = ({
  scope,
  onScopeChange,
  canChangeScope,
  kpiCounts,
}) => {
  const todayFormatted = new Date().toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const cards: KpiItem[] = [
    {
      key: 'openLeads',
      label: 'Open Enquiries',
      count: kpiCounts?.openEnquiries ?? 0,
      icon: Inbox,
      color: 'text-primary',
      bgColor: 'bg-primary-container',
      link: ROUTES.pipeline,
    },
    {
      key: 'openQuotations',
      label: 'Open Quotations',
      count: kpiCounts?.openQuotations ?? 0,
      icon: FileText,
      color: 'text-success-text',
      bgColor: 'bg-success/10',
      link: ROUTES.quotations,
    },
    {
      key: 'openProforma',
      label: 'Open Proforma',
      count: kpiCounts?.openProforma ?? 0,
      icon: FileStack,
      color: 'text-primary',
      bgColor: 'bg-primary-container',
      link: ROUTES.proforma,
    },
    {
      key: 'openSales',
      label: 'Open Sales',
      count: kpiCounts?.openSales ?? 0,
      icon: ShoppingBag,
      color: 'text-info-text',
      bgColor: 'bg-info/10',
      link: ROUTES.salesOperations,
    },
    {
      key: 'totalContacts',
      label: 'Total Contacts',
      count: (kpiCounts?.totalContacts ?? 0).toLocaleString('en-IN'),
      icon: Users,
      color: 'text-info-text',
      bgColor: 'bg-info/10',
      link: ROUTES.contacts,
    },
    {
      key: 'openTasks',
      label: 'Open Tasks',
      count: kpiCounts?.openTasks ?? 0,
      icon: CheckSquare,
      color: 'text-warning-text',
      bgColor: 'bg-warning/10',
      link: ROUTES.tasks,
    },
    {
      key: 'overdueFollowups',
      label: 'Overdue Follow-ups',
      count: kpiCounts?.overdueFollowups ?? 0,
      icon: Clock,
      color: 'text-danger-text',
      bgColor: 'bg-danger/10',
      link: '#followup-center',
    },
    {
      key: 'dueToday',
      label: 'Due Today',
      count: kpiCounts?.dueToday ?? 0,
      icon: CalendarCheck,
      color: 'text-success-text',
      bgColor: 'bg-success/10',
      link: '#followup-center',
    },
  ];

  return (
    <div className="mb-5 rounded-card border border-border bg-card p-4 shadow-xs">
      {/* Header bar: Title + Date + Scope Switcher */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2.5">
        <div className="flex items-center gap-2">
          <h2 className="text-body-sm font-semibold text-on-surface">Today at a glance</h2>
          <time className="rounded-lg bg-surface-variant px-2 py-0.5 text-data-xs text-on-surface-variant" dateTime={new Date().toISOString().slice(0, 10)}>
            {todayFormatted}
          </time>
        </div>

        {/* Scope selector */}
        <div className="flex items-center gap-0.5 rounded-lg border border-border bg-surface-variant p-0.5">
          {(['mine', 'team', 'all'] as Scope[]).map((s) => {
            if (s !== 'mine' && !canChangeScope) return null;

            return (
              <button
                key={s}
                type="button"
                onClick={() => onScopeChange(s)}
                aria-pressed={scope === s}
                className={cn(
                  'rounded-lg px-2.5 py-1 text-body-sm font-medium capitalize transition-colors',
                  scope === s
                    ? 'bg-card text-on-surface shadow-xs'
                    : 'text-on-surface-variant hover:text-on-surface'
                )}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      {/* 8-Card Grid */}
      {/* Four to a row: eight squeezed into one cut every label to "Open Enq…". */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 2xl:grid-cols-8">
        {cards.map((card) => {
          const Icon = card.icon;

          return (
            <Link
              key={card.key}
              to={card.link}
              className="group flex flex-col gap-2 rounded-card-sm border border-border bg-card p-3.5 shadow-xs transition hover:border-primary/30 hover:shadow-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-body-sm font-medium leading-snug text-on-surface-variant">{card.label}</span>
                <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', card.bgColor, card.color)} aria-hidden>
                  <Icon className="h-4 w-4" />
                </span>
              </div>
              <div className="font-mono text-[22px] font-semibold leading-tight tabular-nums text-on-surface">{card.count}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};
